import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { faqStore } from 'store/faqStore'
import { deviceAtom } from 'store/deviceStore'
import { Spacer } from 'wiz-front-shared/components/Spacer'
import { Input } from 'components/common/input/Input'
import { ReactComponent as IcSearch } from 'assets/svgs/icon/ic_search_36.svg'
import { ReactComponent as IcChevron } from 'assets/svgs/icon/ic_next_24.svg'
import './FAQ.css'
import DOMPurify from 'dompurify'
import { Skeleton } from 'wiz-front-shared/components/skeleton/Skeleton'
import { AnimateHeight } from 'wiz-front-shared/components/AnimateHeight'
import { Header } from 'components/common/header/Header'
import { getServiceId } from 'common/service'

interface FaqItem {
    faqNo: number
    question: string
    answer: string
}

interface FaqCategory {
    categoryCode: string
    categoryName: string
    faqList: FaqItem[]
}

// 카테고리별 아코디언으로 전체를 한 화면에 펼치는 구조라 페이징 UI가 없다.
// 서버 조회는 LIMIT 이 걸려 있으므로, 잘려서 접근 불가한 항목이 생기지 않도록 넉넉히 요청한다.
const FAQ_PAGE_SIZE = 150

export const FAQ = () => {
    const { t, i18n } = useTranslation()
    const device = useAtomValue(deviceAtom)
    const inset = device.inset

    const [searchText, setSearchText] = useState('')
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
    const [faqAnswerMap, setFaqAnswerMap] = useState<Record<number, string>>({})
    const [pendingFaqNo, setPendingFaqNo] = useState<number | null>(null)

    // TODO: API 연동 시 주석 해제
    const faqState = useAtomValue(faqStore.faqList.stateAtom)
    const faqDetailState = useAtomValue(faqStore.faqDetail.stateAtom)

    const fetchFaqList = useCallback((searchName?: string) => {
        faqStore.getFaqList({
            serviceId: getServiceId(),
            locale: i18n.language,
            faqType: 'basic',
            searchName: searchName || '',
            pageNo: 1,
            pageSize: FAQ_PAGE_SIZE,
        })
    }, [i18n.language])

    useEffect(() => {
        fetchFaqList()
    }, [fetchFaqList])

    const handleSearch = () => {
        fetchFaqList(searchText.trim())
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const toggleExpand = (faqNo: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev)
            if (next.has(faqNo)) {
                next.delete(faqNo)
            } else {
                next.add(faqNo)
            }
            return next
        })
    }

    const getFaqDetail = (faqItem: FaqItem) => {
        const hasAnswer = !!faqItem.answer
        const hasFetchedAnswer = !!faqAnswerMap[faqItem.faqNo]

        if (hasAnswer || hasFetchedAnswer) return

        setPendingFaqNo(faqItem.faqNo)
        faqStore.getFaqDetail(faqItem.faqNo, getServiceId())
    }

    useEffect(() => {
        if (faqDetailState.isReady) {
            const rawDetail = faqDetailState.data?.data ?? faqDetailState.data
            const detail = rawDetail?.data ?? rawDetail
            const faqNo = Number(detail?.faqNo ?? pendingFaqNo ?? 0)
            const answer = detail?.answer ?? detail?.aContent ?? '등록된 답변이 없습니다.'

            if (faqNo > 0) {
                setFaqAnswerMap((prev) => ({
                    ...prev,
                    [faqNo]: answer,
                }))
            }

            setPendingFaqNo(null)
            faqDetailState.onDefault()
        } else if (faqDetailState.isError) {
            setPendingFaqNo(null)
            faqDetailState.onDefault()
        }
    }, [faqDetailState, pendingFaqNo])

    const rawCategories = faqState.data?.data ?? faqState.data ?? []
    const categories: FaqCategory[] = useMemo(() => {
        if (!Array.isArray(rawCategories)) return []

        return rawCategories.map((category: any) => ({
            categoryCode: category.categoryCode ?? '',
            categoryName: category.categoryName ?? '',
            faqList: Array.isArray(category.faqList)
                ? category.faqList.map((item: any) => {
                    const faqNo = Number(item.faqNo ?? 0)
                    return {
                        faqNo,
                        question: item.question ?? item.qContent ?? '',
                        answer: faqAnswerMap[faqNo] ?? item.answer ?? item.aContent ?? '',
                    }
                })
                : [],
        }))
    }, [rawCategories, faqAnswerMap])

    const handleFaqClick = (item: FaqItem, isExpanded: boolean) => {
        if (!isExpanded) {
            getFaqDetail(item)
        }
        toggleExpand(item.faqNo)
    }

    return (
        <div className="faq">
            <Header title="FAQ" isBack />
            <div className="faq__content" style={{ paddingBottom: 16 + inset.navigationBarHeight }}>
                <div className="faq__search">
                    <Input
                        inputVal={searchText}
                        setInputVal={setSearchText}
                        placeholder={t('faq.searchPlaceholder')}
                        onKeyDown={handleKeyDown}
                        notShowInfo
                        containerStyle={{ padding: 0, flex: 1 }}
                    />
                    <button className="faq__search-btn" onClick={handleSearch}>
                        <IcSearch color="var(--white)" />
                    </button>
                </div>

                <Spacer height={20} />

                {categories.map((category) => (
                    <div key={category.categoryName} className="faq__category">
                        <h2 className="faq__category-title">{category.categoryName}</h2>
                        <Spacer height={12} />
                        <div className="faq__list">
                            {category.faqList.map((item) => {
                                const isExpanded = expandedIds.has(item.faqNo)
                                const isDetailLoading = faqDetailState.isPending && pendingFaqNo === item.faqNo

                                return (
                                    <div
                                        key={item.faqNo}
                                        className={`faq__item ${isExpanded ? 'faq__item--expanded' : ''}`}
                                    >
                                        <button
                                            className="faq__question"
                                            onClick={() => handleFaqClick(item, isExpanded)}
                                        >
                                            <span className="faq__question-text">
                                                <span className="faq__label">[{t('faq.question')}]</span>
                                                {item.question}
                                            </span>
                                            <IcChevron
                                                className={`faq__chevron ${isExpanded ? 'faq__chevron--expanded' : ''}`}
                                                color="var(--darkGray)"
                                            />
                                        </button>
                                        <AnimateHeight className="faq__answer" isOpen={isExpanded}>
                                            <span className="faq__label faq__label--answer">[{t('faq.answer')}]</span>
                                            {/* 스켈레톤 사용 시, 주석해제  */}
                                            {/* <Skeleton width="100%" height={24} borderRadius={4} show={isDetailLoading}>
                                                <div
                                                    className="faq__answer-text"
                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer || '') }}
                                                />
                                            </Skeleton> */}
                                            <div
                                                className="faq__answer-text"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer || '') }}
                                            />
                                        </AnimateHeight>
                                    </div>
                                )
                            })}
                        </div>
                        <Spacer height={24} />
                    </div>
                ))}
            </div>
        </div>
    )
}
